import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import LiveScoresTicker from "@/components/LiveScoresTicker";
import {
    Zap,
    LogIn,
    LogOut,
    LayoutDashboard,
    Calendar,
    MapPin,
    ExternalLink,
    AlertTriangle,
    Activity,
    History,
    Newspaper,
    Trophy,
    ChevronLeft,
} from "lucide-react";

const formatEAT = (iso, withDate = true) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        if (withDate) {
            return d.toLocaleString("en-GB", {
                timeZone: "Africa/Dar_es_Salaam",
                weekday: "long",
                day: "numeric",
                month: "long",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            });
        }
        return d.toLocaleTimeString("en-GB", {
            timeZone: "Africa/Dar_es_Salaam",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
    } catch {
        return "";
    }
};

const FixturePreviewPage = () => {
    const { fixtureId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user, logout, isContentManager } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const league = searchParams.get("league") || "eng.1";
    const sport = searchParams.get("sport") || "soccer";

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const { data: d } = await axios.get(
                    `${API}/fixtures/${fixtureId}/preview?league=${league}&sport=${sport}`
                );
                if (alive) setData(d);
            } catch (e) {
                if (alive) setError(e.response?.data?.detail || "Mechi haikupatikana");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [fixtureId, league, sport]);

    return (
        <div className="min-h-screen bg-obsidian" data-testid="fixture-preview-page">
            {/* Top nav */}
            <nav className="bg-electric">
                <div className="container-main">
                    <div className="flex items-center justify-between h-12">
                        <Link to="/" className="flex items-center gap-2">
                            <Zap className="h-6 w-6 text-white" />
                            <span className="dimbani-wordmark text-xl text-white">DIMBANI</span>
                        </Link>
                        <div className="hidden md:flex items-center gap-3">
                            {user ? (
                                <>
                                    {isContentManager && (
                                        <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} className="text-white hover:bg-white/10">
                                            <LayoutDashboard className="h-4 w-4 mr-1" /> Admin
                                        </Button>
                                    )}
                                    <Button variant="ghost" size="sm" onClick={logout} className="text-white hover:bg-white/10">
                                        <LogOut className="h-4 w-4" />
                                    </Button>
                                </>
                            ) : (
                                <Button variant="ghost" size="sm" onClick={() => navigate("/login")} className="text-white hover:bg-white/10 font-bold">
                                    <LogIn className="h-4 w-4 mr-1" /> Ingia
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            <LiveScoresTicker />

            <div className="container-main py-4">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-1 text-white/60 hover:text-electric text-sm mb-4"
                    data-testid="back-btn"
                >
                    <ChevronLeft className="h-4 w-4" /> Rudi
                </button>

                {loading && (
                    <div className="text-center py-16 text-white/40">Inapakia takwimu za mechi...</div>
                )}
                {error && (
                    <Card className="bg-paper border-border p-12 text-center">
                        <AlertTriangle className="h-10 w-10 text-red-400 mx-auto mb-3" />
                        <p className="text-white">{error}</p>
                    </Card>
                )}

                {data && (
                    <div className="space-y-6">
                        {/* Fixture Hero */}
                        <Card className="bg-paper border-border overflow-hidden">
                            <div className="bg-gradient-to-r from-vodaredDark via-electric to-vodaredDark h-1" />
                            <div className="p-6">
                                <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                                    <Badge className="bg-electric/15 text-electric border-electric/30 uppercase">
                                        {data.fixture.competition_name || "Mashindano"}
                                    </Badge>
                                    <div className="text-white/60 text-sm flex items-center gap-1">
                                        <Calendar className="h-3.5 w-3.5" />
                                        {formatEAT(data.fixture.match_date)} EAT
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 items-center gap-4">
                                    <TeamBlock
                                        name={data.fixture.home_team}
                                        abbr={data.fixture.home_abbr}
                                        logo={data.fixture.home_logo}
                                        side="home"
                                    />
                                    <div className="text-center">
                                        <div className="text-white/40 text-xs uppercase tracking-widest">Mwenyeji vs Mgeni</div>
                                        <div className="font-heading font-black text-4xl md:text-5xl text-white my-2">VS</div>
                                        {data.ai?.prediction_score && (
                                            <div className="mt-2">
                                                <div className="text-white/40 text-[10px] uppercase tracking-wider">Utabiri wa AI</div>
                                                <div className="font-mono text-electric font-bold text-xl">
                                                    {data.ai.prediction_score}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <TeamBlock
                                        name={data.fixture.away_team}
                                        abbr={data.fixture.away_abbr}
                                        logo={data.fixture.away_logo}
                                        side="away"
                                    />
                                </div>

                                {data.fixture.venue && (
                                    <div className="mt-4 pt-4 border-t border-border text-center text-white/60 text-sm flex items-center justify-center gap-1">
                                        <MapPin className="h-3.5 w-3.5" /> {data.fixture.venue}
                                    </div>
                                )}
                            </div>
                        </Card>

                        {/* AI Analysis */}
                        <Section title="Uchambuzi wa Mechi" icon={Activity} testid="section-analysis">
                            <p className="text-white/80 leading-relaxed whitespace-pre-line">
                                {data.ai?.analysis_sw || "Uchambuzi haujapatikana."}
                            </p>
                        </Section>

                        {/* Team News / Injuries */}
                        <Section title="Habari za Timu na Majeruhi" icon={Newspaper} testid="section-team-news">
                            <p className="text-white/80 leading-relaxed whitespace-pre-line">
                                {data.ai?.team_news_sw}
                            </p>
                        </Section>

                        {/* Head to Head */}
                        <Section title="Mikutano ya Awali (H2H)" icon={History} testid="section-h2h">
                            {data.h2h && data.h2h.length > 0 ? (
                                <div className="divide-y divide-border">
                                    {data.h2h.map((h, i) => (
                                        <div
                                            key={i}
                                            className="flex items-center justify-between py-2 text-sm"
                                            data-testid={`h2h-row-${i}`}
                                        >
                                            <span className="text-white/60 text-xs w-24 shrink-0">
                                                {h.date ? new Date(h.date).toLocaleDateString("sw-TZ", { day: "numeric", month: "short", year: "numeric", timeZone: "Africa/Dar_es_Salaam" }) : ""}
                                            </span>
                                            <span className="text-white text-right flex-1 truncate">
                                                {h.home_team}
                                            </span>
                                            <span className="font-mono font-bold text-electric mx-3 whitespace-nowrap">
                                                {h.home_score} - {h.away_score}
                                            </span>
                                            <span className="text-white flex-1 truncate">
                                                {h.away_team}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-white/60 text-sm">
                                    Hakuna mikutano ya awali iliyopatikana kwa hizi timu.
                                </p>
                            )}
                        </Section>

                        {/* AI Prediction */}
                        <Section title="Utabiri wa Mechi (AI)" icon={Trophy} testid="section-prediction">
                            <p className="text-white/80 leading-relaxed mb-4 whitespace-pre-line">
                                {data.ai?.prediction_sw}
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-subtle p-3 rounded border border-border">
                                    <div className="text-white/40 text-[10px] uppercase tracking-wider">Alama Iliyotabiriwa</div>
                                    <div className="font-mono font-black text-2xl text-electric mt-1">
                                        {data.ai?.prediction_score || "—"}
                                    </div>
                                </div>
                                <div className="bg-subtle p-3 rounded border border-border">
                                    <div className="text-white/40 text-[10px] uppercase tracking-wider">Uhakika</div>
                                    <div className="font-mono font-black text-2xl text-electric mt-1">
                                        {Math.round((data.ai?.confidence || 0) * 100)}%
                                    </div>
                                </div>
                            </div>
                            {data.pick && (data.pick.spread || data.pick.over_under) && (
                                <div className="mt-4 pt-4 border-t border-border text-xs text-white/50">
                                    Marejeo ya soko ({data.pick.provider}): Spread {data.pick.spread ?? "—"}, O/U {data.pick.over_under ?? "—"}
                                </div>
                            )}
                        </Section>

                        {/* Disclaimer */}
                        <Card className="bg-red-950/30 border-red-500/40 p-4" data-testid="disclaimer-card">
                            <div className="flex gap-3">
                                <AlertTriangle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                                <p className="text-red-200/90 text-xs leading-relaxed">
                                    {data.disclaimer_sw}
                                </p>
                            </div>
                        </Card>
                    </div>
                )}
            </div>
        </div>
    );
};

const Section = ({ title, icon: Icon, children, testid }) => (
    <Card className="bg-paper border-border p-5" data-testid={testid}>
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border">
            <Icon className="h-4 w-4 text-electric" />
            <h2 className="font-heading font-bold text-sm text-white uppercase tracking-wider">
                {title}
            </h2>
        </div>
        {children}
    </Card>
);

const TeamBlock = ({ name, abbr, logo, side }) => (
    <div className={`flex flex-col items-center ${side === "away" ? "text-right" : "text-left"}`}>
        {logo ? (
            <img src={logo} alt={name} className="h-20 w-20 object-contain mb-2" onError={(e) => (e.currentTarget.style.display = "none")} />
        ) : (
            <div className="h-20 w-20 rounded-full bg-subtle border border-border flex items-center justify-center mb-2">
                <span className="text-white/50 font-mono font-bold">{abbr || "?"}</span>
            </div>
        )}
        <div className="font-heading font-bold text-sm md:text-base text-white text-center line-clamp-2">
            {name}
        </div>
        {abbr && <div className="text-white/40 text-[10px] font-mono">{abbr}</div>}
    </div>
);

export default FixturePreviewPage;
